import { getApps, getApp, initializeApp, FirebaseError } from 'firebase/app';
import { getAuth, connectAuthEmulator, onAuthStateChanged, createUserWithEmailAndPassword, sendEmailVerification as sendEmailVerification$1, applyActionCode, signInWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset, signOut, updateEmail, updatePassword, getIdToken, reload } from 'firebase/auth';
import { getStorage, connectStorageEmulator, ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { getFirestore, connectFirestoreEmulator, Timestamp, doc, getDoc, setDoc, query, collection, where, getDocs, orderBy, limit, startAfter, updateDoc, collectionGroup, addDoc } from 'firebase/firestore';
import { pick } from 'lodash';
import { add } from 'date-fns';
import { v4 } from 'uuid';

/* eslint-disable unicorn/prefer-node-protocol */
let firestore;
let auth;
let storage;
const init = () => {
    if (typeof window !== 'object')
        return;
    if (getApps().length > 0) {
        const app = getApp();
        firestore = getFirestore(app);
        auth = getAuth(app);
        storage = getStorage(app);
        return;
    }
    const firebaseConfig = {
        apiKey: process.env.GATSBY_FIREBASE_API_KEY,
        authDomain: process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
        storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.GATSBY_FIREBASE_APP_ID,
        measurementId: process.env.GATSBY_FIREBASE_MEASUREMENT_ID,
    };
    initializeApp(firebaseConfig);
    firestore = getFirestore();
    auth = getAuth();
    storage = getStorage();
    if (['127.0.0.1', 'localhost'].includes(window.location.hostname)) {
        const { emulators } = process.env.NODE_ENV === 'test'
            ? require('@codesass/emu/firebase.test.json')
            : require('@codesass/emu/firebase.json');
        connectFirestoreEmulator(firestore, 'localhost', emulators.firestore.port);
        connectAuthEmulator(auth, `http://localhost:${emulators.auth.port}/`, {
            disableWarnings: true,
        });
        connectStorageEmulator(storage, 'localhost', emulators.storage.port);
    }
};

const isFirebaseAuthError = (error) => {
    return error instanceof Error && error.name === 'FirebaseError';
};
const isFirebaseError = (error) => {
    return error instanceof Error && error.name === 'FirebaseError';
};
const isFirestoreError = (error) => {
    return error instanceof Error && error.name === 'FirestoreError';
};

const timestampToDate = (timestamp) => {
    return new Intl.DateTimeFormat('th-Th', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(timestamp.toDate());
};
const dateToTimestamp = (date) => {
    return Timestamp.fromDate(date);
};

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get firestore () { return firestore; },
  get auth () { return auth; },
  get storage () { return storage; },
  init: init,
  isFirebaseAuthError: isFirebaseAuthError,
  isFirebaseError: isFirebaseError,
  isFirestoreError: isFirestoreError,
  timestampToDate: timestampToDate,
  dateToTimestamp: dateToTimestamp
});

const COMMON_ERROR_MESSAGE = 'เกิดข้อผิดพลาดกับระบบ โปรดลองอีกครั้ง';
const getErrorMessage = (mapping, error) => {
    if (!isFirebaseAuthError(error)) {
        return COMMON_ERROR_MESSAGE;
    }
    const authError = mapping[error.code];
    return authError ?? COMMON_ERROR_MESSAGE;
};
const selectProfileField = (user) => {
    return pick(user, [
        'uid',
        'displayName',
        'email',
        'emailVerified',
        'photoURL',
    ]);
};

async function getCurrentUser(full = false) {
    return new Promise(resolve => {
        onAuthStateChanged(getAuth(), user => {
            if (!user)
                return resolve(null);
            resolve(full ? user : selectProfileField(user));
        });
    });
}

class LoginRequiredError extends Error {
    constructor(message = 'Login Required') {
        super(message);
        this.name = 'LoginRequiredError';
    }
}
class VerifyEmailError extends Error {
    constructor(message) {
        super(message);
        this.name = 'VerifyEmailError';
    }
}
class LoginError extends Error {
    constructor(message) {
        super(message);
        this.name = 'LoginError';
    }
}
class UpdateProfileError extends Error {
    field;
    constructor(field, message) {
        super(message);
        this.field = field;
        this.name = 'UpdateProfileError';
    }
}

const getProfile = async (uid) => {
    let id = uid;
    if (!uid) {
        const user = await getCurrentUser();
        if (!user)
            throw new LoginRequiredError();
        id = user.uid;
    }
    const profileRef = doc(firestore, `/users/${id}`);
    const profileSnapshot = await getDoc(profileRef);
    const profile = profileSnapshot.data();
    if (profile.avatar) {
        const avatarRef = ref(storage, profile.avatar);
        profile.avatarPath = profile.avatar;
        profile.avatar = await getDownloadURL(avatarRef);
    }
    return profile;
};

const CREATE_USER_ERROR_MAPPING = {
    'auth/email-already-in-use': 'อีเมลดังกล่าวถูกใช้ในการสมัครสมาชิกแล้ว',
    'auth/invalid-email': 'รูปแบบอีเมลไม่ถูกต้อง',
    'auth/operation-not-allowed': 'การลงทะเบียนยังไม่เปิดใช้งาน',
    'auth/weak-password': 'รหัสผ่านไม่ปลอดภัย โปรดแก้ไขรหัสผ่านอีกครั้ง',
    'auth/invalid-continue-uri': 'เกิดข้อผิดพลาดในการส่งการยืนยันอีเมล โปรดติดต่อผู้ดูแลระบบ',
};
const createUser = async ({ email, password }) => {
    try {
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        const profileRef = doc(firestore, `/users/${user.uid}`);
        await sendEmailVerification$1(user);
        await setDoc(profileRef, { email, uid: user.uid, emailVerified: false });
        return await getProfile();
    }
    catch (error) {
        const message = getErrorMessage(CREATE_USER_ERROR_MAPPING, error);
        throw new Error(message);
    }
};

const SEND_EMAIL_VERIFICATION_ERROR_MAPPING = {
    'auth/email-already-in-use': 'อีเมลดังกล่าวถูกใช้ในการสมัครสมาชิกแล้ว',
    'auth/invalid-email': 'รูปแบบอีเมลไม่ถูกต้อง',
    'auth/invalid-continue-uri': 'เกิดข้อผิดพลาดในการส่งการยืนยันอีเมล โปรดติดต่อผู้ดูแลระบบ',
};
const sendEmailVerification = async () => {
    try {
        const user = await getCurrentUser(true);
        if (!user)
            return;
        await sendEmailVerification$1(user);
    }
    catch (error) {
        const message = getErrorMessage(SEND_EMAIL_VERIFICATION_ERROR_MAPPING, error);
        throw new Error(message);
    }
};

const VERIFY_EMAIL_ERROR_MAPPING = {
    'auth/expired-action-code': 'รหัสการยืนยันอีเมล์หมดอายุ',
    'auth/invalid-action-code': 'รูปแบบรหัสการยืนยันอีเมล์ไม่ถูกต้อง',
    'auth/user-disabled': 'ผู้ใช้งานถูกปิดการเข้าถึง กรุณาติดต่อผู้ดูแลระบบ',
    'auth/user-not-found': 'ไม่พบผู้ใช้งานดังกล่าว',
};
const verifyEmail = async (oobCode) => {
    try {
        const user = await getCurrentUser();
        if (!user) {
            throw new VerifyEmailError('กรุณาเข้าสู่ระบบก่อนการยืนยันอีเมล์');
        }
        await applyActionCode(auth, oobCode);
        const profileRef = doc(firestore, `/users/${user.uid}`);
        await setDoc(profileRef, { emailVerified: true }, { merge: true });
    }
    catch (error) {
        throw new VerifyEmailError(getErrorMessage(VERIFY_EMAIL_ERROR_MAPPING, error));
    }
};

const LOGIN_ERROR_MAPPING = {
    'auth/user-disabled': 'บัญชีดังกล่าวถูกระงับการใช้งาน',
    'auth/user-not-found': 'อีเมลหรือรหัสผ่านไม่ถูกต้อง โปรดตรวจสอบอีกครั้ง',
    'auth/wrong-password': 'อีเมลหรือรหัสผ่านไม่ถูกต้อง โปรดตรวจสอบอีกครั้ง',
};
const login = async ({ email, password }) => {
    try {
        await signInWithEmailAndPassword(auth, email, password);
        return getProfile();
    }
    catch (error) {
        throw new LoginError(getErrorMessage(LOGIN_ERROR_MAPPING, error));
    }
};

const sendResetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
};

const resetPassword = async (code, password) => {
    return confirmPasswordReset(auth, code, password);
};

const logout = () => {
    return signOut(auth);
};

const UPDATE_PROFILE_ERROR_MAPPING = {
    'auth/requires-recent-login': 'เพื่อความปลอดภัยของการบันทึกข้อมูล กรุณาเข้าสู่ระบบใหม่อีกครั้งก่อนการแก้ไขข้อมูลส่วนตัว',
};
const updateProfile = async ({ email, password, avatar, ...profile }) => {
    const user = getAuth().currentUser;
    if (!user)
        return;
    const newProfile = {
        email,
        ...profile,
    };
    const usernameQuery = query(collection(firestore, 'users'), where('username', '==', profile.username), where('uid', '!=', user.uid));
    const withUsernameSnapshot = await getDocs(usernameQuery);
    if (!withUsernameSnapshot.empty) {
        throw new UpdateProfileError('username', 'มีการลงทะเบียนชื่อผู้ใช้งานนี้ในระบบแล้ว');
    }
    if (email && email !== user.email) {
        await updateEmail(user, email);
        newProfile.emailVerified = false;
    }
    if (password) {
        await updatePassword(user, password);
    }
    if (avatar) {
        const avatarRef = ref(storage, `users/${user.uid}/avatar/${avatar.name}`);
        const avatarSnapshot = await uploadBytes(avatarRef, avatar);
        newProfile.avatar = avatarSnapshot.metadata.fullPath;
    }
    const userRef = doc(firestore, `/users/${user.uid}`);
    try {
        await setDoc(userRef, newProfile, { merge: true });
    }
    catch (error) {
        const message = getErrorMessage(UPDATE_PROFILE_ERROR_MAPPING, error);
        throw new Error(message);
    }
};

const reloadCurrentUser = async () => {
    const user = await getCurrentUser(true);
    if (!user)
        return;
    getIdToken(user, true);
    reload(user);
};

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createUser: createUser,
  sendEmailVerification: sendEmailVerification,
  verifyEmail: verifyEmail,
  login: login,
  sendResetPassword: sendResetPassword,
  resetPassword: resetPassword,
  logout: logout,
  getCurrentUser: getCurrentUser,
  getProfile: getProfile,
  updateProfile: updateProfile,
  reloadCurrentUser: reloadCurrentUser,
  LoginRequiredError: LoginRequiredError,
  VerifyEmailError: VerifyEmailError,
  LoginError: LoginError,
  UpdateProfileError: UpdateProfileError
});

const GET_LESSON_ERRORS$1 = {
    'invalid-argument': 'URL สำหรับการเข้าถึงบทเรียนไม่มีอยู่จริง',
    'not-found': 'ไม่พบบทเรียนดังกล่าว',
    'permission-denied': 'โปรดลงทะเบียนเพื่อเข้าถึงเนื้อหาของบทเรียนนี้',
    default: 'พบข้อผิดพลาดบางประการ โปรดติดต่อผู้ดูแล',
};
const getLesson = async (courseSlug, unitSlug, lessonSlug) => {
    try {
        const docRef = doc(firestore, `courses/${courseSlug}/units/${unitSlug}/lessons/${lessonSlug}`);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists())
            throw new FirebaseError('not-found', 'Not Found');
        return docSnap.data();
    }
    catch (error) {
        throw isFirestoreError(error) || isFirebaseError(error)
            ? new Error(GET_LESSON_ERRORS$1[error.code in GET_LESSON_ERRORS$1 ? error.code : 'default'])
            : new Error(GET_LESSON_ERRORS$1['default']);
    }
};

const GET_LESSON_ERRORS = {
    'invalid-argument': 'URL สำหรับการเข้าถึงบทเรียนไม่มีอยู่จริง',
    'not-found': 'ไม่พบบทเรียนดังกล่าว',
    'permission-denied': 'โปรดลงทะเบียนเพื่อเข้าถึงเนื้อหาของบทเรียนนี้',
    default: 'พบข้อผิดพลาดบางประการ โปรดติดต่อผู้ดูแล',
};
const getOverview = async (courseSlug) => {
    try {
        const docRef = doc(firestore, `courses/${courseSlug}/units/overview/lessons/overview`);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists())
            throw new FirebaseError('not-found', 'Not Found');
        return docSnap.data();
    }
    catch (error) {
        throw isFirestoreError(error) || isFirebaseError(error)
            ? new Error(GET_LESSON_ERRORS[error.code in GET_LESSON_ERRORS ? error.code : 'default'])
            : new Error(GET_LESSON_ERRORS['default']);
    }
};

const GET_TOC_ERRORS = {
    'not-found': 'ไม่พบบทเรียนดังกล่าว',
    default: 'พบข้อผิดพลาดบางประการ โปรดติดต่อผู้ดูแล',
};
const getToc = async (slug) => {
    try {
        const docRef = doc(firestore, `courses/${slug}`);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists())
            throw new FirebaseError('not-found', 'Not Found');
        return docSnap.data()['toc'];
    }
    catch {
        throw new Error(GET_TOC_ERRORS.default);
    }
};

const StatusMapping = {
    Viewing: 0,
    Done: 1,
};

const setLessonViewStatus = async (params) => {
    const { type, courseSlug, lessonSlug, status } = params;
    const user = await getCurrentUser();
    if (!user)
        return;
    const ref = doc(firestore, `/users/${user.uid}/courses/${courseSlug}`);
    const getParams = () => {
        switch (type) {
            case 'Video':
                // eslint-disable-next-line unicorn/consistent-destructuring
                return { seconds: params.seconds };
            case 'Exam':
                // eslint-disable-next-line unicorn/consistent-destructuring
                return { score: params.score };
            case 'CertificationExam':
                return {};
        }
    };
    try {
        await setDoc(ref, {
            [lessonSlug]: {
                type,
                status: StatusMapping[status],
                ...getParams(),
            },
        }, { merge: true });
    }
    catch (error) {
        if (isFirebaseError(error)) ;
    }
};

const GET_LESSON_VIEW_STATUSES_ERRORS = {
    'invalid-argument': 'URL สำหรับการเข้าถึงค่าสถานะการรับชมบทเรียนไม่มีอยู่จริง',
    'not-found': 'ไม่พบบทเรียนดังกล่าว',
    default: 'พบข้อผิดพลาดบางประการ โปรดติดต่อผู้ดูแล',
};
const getLessonViewStatuses = async (courseSlug) => {
    const user = await getCurrentUser();
    if (!user)
        return;
    try {
        const ref = doc(firestore, `/users/${user.uid}/courses/${courseSlug}`);
        const docSnap = await getDoc(ref);
        if (!docSnap.exists())
            throw new FirebaseError('not-found', 'Not Found');
        return docSnap.data();
    }
    catch (error) {
        throw isFirestoreError(error)
            ? new Error(GET_LESSON_VIEW_STATUSES_ERRORS['invalid-argument'])
            : new Error(GET_LESSON_VIEW_STATUSES_ERRORS.default);
    }
};

const register = async ({ slug, title, price, promotion, slip, }) => {
    const user = await getCurrentUser();
    if (!user)
        throw new LoginRequiredError();
    const { uid } = user;
    const storageRef = ref(storage, `users/${uid}/registrations/${slug}/${slip.name}`);
    const slipSnapshot = await uploadBytes(storageRef, slip);
    const docRef = doc(firestore, `/users/${uid}/registrations/${slug}`);
    const currentDate = new Date();
    setDoc(docRef, {
        slug,
        title,
        uid,
        couponId: null,
        createdAt: currentDate,
        updatedAt: currentDate,
        expiredAt: add(currentDate, { years: 3 }),
        approved: false,
        price,
        promotion: promotion ?? 0,
        slip: slipSnapshot.metadata.fullPath,
    });
};

const getRegistration = async (slug) => {
    const user = await getCurrentUser();
    if (!user)
        throw new LoginRequiredError();
    const ref = doc(firestore, `/users/${user.uid}/registrations/${slug}`);
    const snapshpt = await getDoc(ref);
    return snapshpt.data();
};

const GET_REGISTRATIONS_COURSES_ERROR_MAPPING = {
    'storage/object-not-found': 'ไม่พบไฟล์หลักฐานการชำระเงิน',
    'storage/unauthorized': 'ผู้ใช้งานไม่ได้รับอนุญาตให้เข้าถึงไฟล์นี้',
    'storage/unknown': 'เกิดข้อผิดพลาดที่ไม่สามารถระบุได้ โปรดติดต่อผู้ดูแลระบบ',
};
const getRegisteredCourses = async (lastVisible) => {
    const user = await getCurrentUser();
    if (!user)
        throw new LoginRequiredError();
    const queryOptions = [orderBy('createdAt', 'desc'), limit(10)];
    if (lastVisible)
        queryOptions.push(startAfter(lastVisible));
    const docQuery = query(collection(firestore, `users/${user.uid}/registrations`), ...queryOptions);
    const snapshots = await getDocs(docQuery);
    try {
        const courses = snapshots.docs.map(async (doc) => {
            const data = doc.data();
            const imageRef = ref(storage, `courses/${data.slug}/feature-image.png`);
            return {
                ...data,
                courseImage: await getDownloadURL(imageRef),
            };
        });
        return {
            lastVisible: snapshots.docs[snapshots.docs.length - 1],
            courses: await Promise.all(courses),
        };
    }
    catch (error) {
        if (isFirestoreError(error)) {
            GET_REGISTRATIONS_COURSES_ERROR_MAPPING[error.code];
        }
    }
};

const updateRegistration = (uid, slug, registration) => {
    const ref = doc(firestore, `/users/${uid}/registrations/${slug}`);
    return updateDoc(ref, registration);
};

const setCertificationExamStatus = async ({ uid, id, courseSlug, courseName, certificationTitle, status, verified = false, }) => {
    let userId = uid;
    if (!userId) {
        const user = await getCurrentUser();
        if (!user)
            throw new LoginRequiredError();
        userId = user.uid;
    }
    const ref = doc(firestore, `/users/${userId}/cert-exams/${courseSlug}`);
    const data = {
        ...status,
        uid: userId,
        courseSlug,
        courseName,
        verified,
    };
    if (id)
        data['id'] = id;
    if (certificationTitle)
        data['certificationTitle'] = certificationTitle;
    return setDoc(ref, data, { merge: true });
};

const getCertificationExamStatus = async (slug, uid) => {
    const user = await getCurrentUser();
    if (!user)
        return;
    const ref = doc(firestore, `/users/${uid ?? user.uid}/cert-exams/${slug}`);
    const snapshot = await getDoc(ref);
    return snapshot.data();
};

const getUnVerifiedCertificationExamStatuses = async () => {
    const certExamsStatusesQuery = query(collectionGroup(firestore, 'cert-exams'), where('type', 'in', ['done', 'timeExceed']), where('verified', '!=', true));
    const certExamsStatusesSnapshot = (await getDocs(certExamsStatusesQuery));
    return certExamsStatusesSnapshot.docs.map(doc => doc.data());
};

const getCertificationExam = async (slug) => {
    const ref = doc(firestore, `/cert-exams/${slug}`);
    const snapshot = await getDoc(ref);
    return snapshot.data();
};

const verifyCertification = async ({ uid, courseSlug, courseName, certificationTitle, }) => {
    const profile = await getProfile(uid);
    const id = v4();
    const status = await getCertificationExamStatus(courseSlug, uid);
    const { answers } = await getCertificationExam(courseSlug);
    if (!status)
        return;
    if (status.type === 'start')
        return;
    let correctAnswerCount = 0;
    for (const [i, answer] of answers.entries()) {
        if (status.answers[i] === answer)
            correctAnswerCount++;
    }
    const score = (correctAnswerCount * 100) / answers.length;
    const ref = doc(firestore, `/certifications/${id}`);
    const data = {
        id,
        score,
        courseSlug,
        courseName,
        certificationTitle,
        passed: score >= 70,
        uid: profile.uid,
        name: profile.name,
        verifiedAt: new Date(),
    };
    setDoc(ref, data, { merge: false });
    await setCertificationExamStatus({
        uid: profile.uid,
        id,
        courseSlug,
        courseName,
        status: undefined,
        verified: true,
    });
};

const getCertification = async (id) => {
    const ref = doc(firestore, `/certifications/${id}`);
    const snapshot = await getDoc(ref);
    return snapshot.data();
};

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getLesson: getLesson,
  getOverview: getOverview,
  getToc: getToc,
  setLessonViewStatus: setLessonViewStatus,
  getLessonViewStatuses: getLessonViewStatuses,
  register: register,
  getRegistration: getRegistration,
  getRegisteredCourses: getRegisteredCourses,
  updateRegistration: updateRegistration,
  setCertificationExamStatus: setCertificationExamStatus,
  getCertificationExamStatus: getCertificationExamStatus,
  getUnVerifiedCertificationExamStatuses: getUnVerifiedCertificationExamStatuses,
  verifyCertification: verifyCertification,
  getCertificationExam: getCertificationExam,
  getCertification: getCertification,
  StatusMapping: StatusMapping
});

const getRegistrations = async (status = 'All') => {
    const group = collectionGroup(firestore, 'registrations');
    const registrationsQuery = status === 'All'
        ? query(group)
        : query(group, where('approved', '==', status === 'Approved'));
    const registrationsSnapshot = (await getDocs(registrationsQuery));
    return registrationsSnapshot.docs.map(doc => doc.data());
};

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getRegistrations: getRegistrations
});

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  courses: index$3
});

const getPath = (props) => {
    switch (props.type) {
        case 'course':
            return `/comments/courses/${props.slug}`;
        case 'lesson':
            return `/comments/courses/${props.courseSlug}/lessons/${props.lessonSlug}`;
        case 'article':
            return `/comments/blog/${props.slug}`;
    }
};

const addComment = async (props) => {
    const { type, content, rating } = props;
    const profile = await getProfile();
    if (!profile)
        throw new LoginRequiredError();
    const ref = collection(firestore, getPath(props));
    try {
        switch (type) {
            case 'lesson':
                await addDoc(ref, {
                    type,
                    content,
                    rating: rating ?? 0,
                    uid: profile.uid,
                    username: profile.username,
                    avatar: profile.avatarPath ?? null,
                    createdAt: new Date(),
                });
                break;
            case 'article':
                await addDoc(ref, {
                    type,
                    content,
                    rating: rating ?? 0,
                    uid: profile.uid,
                    username: profile.username,
                    avatar: profile.avatarPath ?? null,
                    createdAt: new Date(),
                });
                break;
        }
    }
    catch (error) {
        if (isFirebaseError(error)) ;
    }
};

const replyComment = async (props) => {
    const { id, type, content, rating } = props;
    const profile = await getProfile();
    if (!profile)
        throw new LoginRequiredError();
    const ref = doc(firestore, `${getPath(props)}/${id}`);
    const comment = await getDoc(ref);
    const replies = comment.data().replies ?? [];
    try {
        switch (type) {
            case 'lesson':
                await updateDoc(ref, {
                    replies: [
                        {
                            type,
                            content,
                            rating: rating ?? 0,
                            uid: profile.uid,
                            username: profile.username,
                            avatar: profile.avatarPath ?? null,
                            createdAt: new Date(),
                        },
                        ...replies,
                    ],
                });
                break;
            case 'article':
                await updateDoc(ref, {
                    replies: [
                        {
                            type,
                            content,
                            rating: rating ?? 0,
                            uid: profile.uid,
                            username: profile.username,
                            avatar: profile.avatarPath ?? null,
                            createdAt: new Date(),
                        },
                        ...replies,
                    ],
                });
                break;
        }
    }
    catch (error) {
        if (isFirebaseError(error)) ;
    }
};

const getComments = async ({ lastVisible, ...props }) => {
    const queryOptions = [orderBy('createdAt', 'desc'), limit(10)];
    if (lastVisible)
        queryOptions.push(startAfter(lastVisible));
    const docQuery = query(collection(firestore, getPath(props)), ...queryOptions);
    try {
        const snapshots = await getDocs(docQuery);
        const comments = snapshots.docs.map(async (doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
            };
        });
        return {
            lastVisible: snapshots.docs[snapshots.docs.length - 1],
            comments: await Promise.all(comments),
        };
    }
    catch (error) {
        if (isFirestoreError(error)) ;
    }
};

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addComment: addComment,
  replyComment: replyComment,
  getComments: getComments
});

const GET_STORAGE_DOWNLOAD_URL_ERROR_MAPPING = {
    'storage/object-not-found': 'ไม่พบไฟล์หลักฐานการชำระเงิน',
    'storage/unauthorized': 'ผู้ใช้งานไม่ได้รับอนุญาตให้เข้าถึงไฟล์นี้',
    'storage/unknown': 'เกิดข้อผิดพลาดที่ไม่สามารถระบุได้ โปรดติดต่อผู้ดูแลระบบ',
};
const getStorageDownloadUrl = (path) => {
    try {
        const imageRef = ref(storage, path);
        return getDownloadURL(imageRef);
    }
    catch (error) {
        if (isFirestoreError(error)) {
            GET_STORAGE_DOWNLOAD_URL_ERROR_MAPPING[error.code];
        }
    }
};

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getStorageDownloadUrl: getStorageDownloadUrl
});

export { index$2 as admin, index$5 as auth, index$1 as comments, index$4 as courses, index$6 as shared, index as utils };
